import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useEmitter } from 'dashboard/composables/emitter';
import ConversationApi from 'dashboard/api/inbox/conversation';
import {
  CHANNEL_TAB,
  isEmailInbox,
  isWhatsappInbox,
} from 'dashboard/constants/gch';

// Tabs of "Mi bandeja de entrada": every channel, Email or WhatsApp, each one
// with the number of my conversations that match the active filters.
export function useMineChannelTabs({ isMineView, filters }) {
  const { t } = useI18n();
  const inboxes = useMapGetter('inboxes/getInboxes');

  const activeChannelTab = ref(CHANNEL_TAB.ALL);
  const counts = ref({});

  const inboxIdsByTab = computed(() => ({
    [CHANNEL_TAB.EMAIL]: inboxes.value.filter(isEmailInbox).map(({ id }) => id),
    [CHANNEL_TAB.WHATSAPP]: inboxes.value
      .filter(isWhatsappInbox)
      .map(({ id }) => id),
  }));

  // A channel without inboxes has nothing to show, so it gets no tab.
  const tabKeys = computed(() => [
    CHANNEL_TAB.ALL,
    ...Object.keys(inboxIdsByTab.value).filter(
      key => inboxIdsByTab.value[key].length
    ),
  ]);

  const channelTabs = computed(() =>
    tabKeys.value.map(key => ({
      key,
      name: t(`CHAT_LIST.CHANNEL_TABS.${key}`),
      count: counts.value[key] || 0,
    }))
  );

  const channelInboxIds = computed(() =>
    isMineView.value ? inboxIdsByTab.value[activeChannelTab.value] : undefined
  );

  const fetchCounts = async () => {
    if (!isMineView.value) return;
    try {
      const entries = await Promise.all(
        tabKeys.value.map(async key => {
          const { data } = await ConversationApi.meta({
            ...filters.value,
            inboxId: inboxIdsByTab.value[key],
          });
          return [key, data.meta.mine_count];
        })
      );
      counts.value = Object.fromEntries(entries);
    } catch (error) {
      // The counters are refreshed on the next conversation event.
    }
  };

  const debouncedFetchCounts = debounce(fetchCounts, 1000, false, 5000);

  watch([isMineView, filters, tabKeys], fetchCounts, { immediate: true });
  useEmitter('fetch_conversation_stats', debouncedFetchCounts);

  return { activeChannelTab, channelTabs, channelInboxIds };
}
